import { API_CONFIG } from '../config';

/**
 * Enhanced fetch wrapper with automatic token handling and 401 redirect
 */
export const apiFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = localStorage.getItem('spellbook_token');
  
  // Automatically add Authorization header if token exists
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add Content-Type for JSON requests if not already set
  // Don't set Content-Type for FormData - browser will set it automatically with boundary
  if (!headers['Content-Type'] && options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }
  // If body is FormData, don't set Content-Type header (browser will set it automatically)
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  // Check for 401 Unauthorized or 403 Forbidden
  if (response.status === 401 || response.status === 403) {
    const errorData = await response.text();
    
    // Check if it's a token expiration error
    if (errorData.includes('expired') || errorData.includes('Token has expired')) {
      console.warn('🔒 Token has expired. Redirecting to login...');
      
      // Clear stored token and user data
      localStorage.removeItem('spellbook_token');
      localStorage.removeItem('spellbook_user');
      
      // Redirect to login page
      window.location.href = '/';
      
      // Throw error to prevent further processing
      throw new Error('Token expired - redirecting to login');
    }
    
    // For other 401/403 errors
    console.warn('🔒 Authentication failed. Please log in again.');
    localStorage.removeItem('spellbook_token');
    localStorage.removeItem('spellbook_user');
    window.location.href = '/';
    throw new Error('Authentication failed');
  }
  
  return response;
};

/**
 * Convenience method for GET requests
 */
export const apiGet = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return apiFetch(url, {
    ...options,
    method: 'GET'
  });
};

/**
 * Convenience method for POST requests
 */
export const apiPost = async (
  url: string,
  body?: any,
  options: RequestInit = {}
): Promise<Response> => {
  return apiFetch(url, {
    ...options,
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body)
  });
};

/**
 * Convenience method for PUT requests
 */
export const apiPut = async (
  url: string,
  body?: any,
  options: RequestInit = {}
): Promise<Response> => {
  return apiFetch(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body)
  });
};

/**
 * Convenience method for DELETE requests
 */
export const apiDelete = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return apiFetch(url, {
    ...options,
    method: 'DELETE'
  });
};

